import { ScenePreview } from "@cva/renderer";
import {
  applyMergedPages,
  buildMergedDocument,
  columnKey,
  columnUsage,
  type DataTable,
  isImagePlaceholder,
  isMappable,
  MERGE_MAX_FILES,
  MERGE_MAX_PAGES,
  type MergeMapping,
  type MergeOptions,
  mergePage,
  mergePageCapacity,
  mergeTitle,
  normalizeMergedGroups,
  pageElementIds,
  parseTable,
  placeholderNames,
  type SceneDocument,
  type SceneElement,
  tableRecords,
} from "@cva/shared";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Download,
  FileUp,
  Plus,
  TableProperties,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import type { EditorSearchCommand } from "../components/search-palette";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogFooter } from "../components/ui/dialog";
import { Input, Select, Textarea } from "../components/ui/input";
import { client, orpc } from "../lib/api";
import { cn, errorMessage } from "../lib/utils";
import { fitMergedTexts } from "./bulk-fit";
import { useEnv } from "./env";
import { useEditor, useEditorApi } from "./store";

/**
 * „Serie erstellen“: one copy of the open page per table row (mail merge).
 * The pure part (CSV, placeholders, cloning) lives in `@cva/shared`
 * (scene/merge.ts); this dialog reads the data, resolves image file names in
 * the media library, fits long texts and applies all pages in one `update`
 * (one undo step, sent to the others as one set of live ops).
 */

type Source = "file" | "paste" | "grid";
type Output = "after" | "replace" | "files";

const GRID_ROWS_SHOWN = 50;
const EMPTY_TABLE: DataTable = { headers: [], rows: [] };

/** CSV files from Excel are often Windows-1252; UTF-8 (with or without BOM) otherwise. */
async function readTextFile(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(buffer);
  } catch {
    return new TextDecoder("windows-1252").decode(buffer);
  }
}

function truncate(text: string, length: number): string {
  const flat = text.replace(/\s+/g, " ").trim();
  return flat.length > length ? `${flat.slice(0, length - 1)}…` : flat;
}

function elementLabel(element: SceneElement): string {
  const kind =
    element.type === "text"
      ? `Text „${truncate(element.text, 28)}“`
      : element.type === "qr"
        ? `QR-Code (${truncate(element.value, 24)})`
        : element.type === "image"
          ? "Bild"
          : "Bildplatzhalter";
  return element.name ? `${element.name} – ${kind}` : kind;
}

const normalizeName = (value: string) => value.trim().toLocaleLowerCase("de");
const stemName = (value: string) => normalizeName(value).replace(/\.[a-z0-9]{2,5}$/, "");

/** Media library assets for image file names ("anna.jpg", "Anna"), keyed by `normalizeName`. */
async function resolveImageNames(orgId: string, values: string[]): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  const unique = [...new Set(values.map((value) => value.trim()).filter(Boolean))];
  for (let index = 0; index < unique.length; index += 6) {
    await Promise.all(
      unique.slice(index, index + 6).map(async (value) => {
        const { items } = await client.media.list({
          orgId,
          kind: "image",
          search: stemName(value).slice(0, 200),
          limit: 50,
        });
        const hit =
          items.find((asset) => normalizeName(asset.name) === normalizeName(value)) ??
          items.find((asset) => stemName(asset.name) === stemName(value));
        if (hit) out.set(normalizeName(value), hit.id);
      }),
    );
  }
  return out;
}

/** ⌘K entry of the editor. */
export function bulkCreateCommand(run: () => void): EditorSearchCommand {
  return {
    id: "bulk-create",
    title: "Serie erstellen …",
    subtitle: "Seiten oder Dateien aus einer Tabelle (CSV, Excel)",
    keywords: [
      "Serienbrief",
      "Seriendruck",
      "CSV",
      "Excel",
      "Tabelle",
      "Gutscheine",
      "Namensschilder",
    ],
    icon: <TableProperties />,
    run,
  };
}

export function BulkCreateDialog({
  open,
  onOpenChange,
  onExport,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Opens the export dialog (after pages were created). */
  onExport: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title="Serie erstellen"
        description={
          <>
            Erstellt aus einer Tabelle eine Seite oder Datei je Zeile – z. B. Gutscheine,
            Namensschilder oder Tickets. Schreibe dazu Platzhalter wie <code>{"{{Name}}"}</code> in
            Texte oder QR-Codes der aktuellen Seite.
          </>
        }
        className="max-h-[calc(100vh-2rem)] max-w-5xl overflow-y-auto"
      >
        {open ? <BulkCreateBody onClose={() => onOpenChange(false)} onExport={onExport} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function BulkCreateBody({ onClose, onExport }: { onClose: () => void; onExport: () => void }) {
  const env = useEnv();
  const store = useEditorApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const doc = useEditor((s) => s.doc);
  const pageIndex = useEditor((s) => s.pageIndex);

  const [source, setSource] = useState<Source>("file");
  const [table, setTable] = useState<DataTable>(EMPTY_TABLE);
  const [fileName, setFileName] = useState<string | null>(null);
  const [pasted, setPasted] = useState("");
  const [mappings, setMappings] = useState<MergeMapping[]>([]);
  const [output, setOutput] = useState<Output>("after");
  const [namePattern, setNamePattern] = useState("");
  const [shrink, setShrink] = useState(true);
  const [previewRow, setPreviewRow] = useState(0);
  const [images, setImages] = useState<Map<string, string>>(new Map());
  const [busy, setBusy] = useState<string | null>(null);
  const [done, setDone] = useState<{
    kind: "pages" | "files";
    count: number;
    /** The template page with the placeholders is still in the document. */
    templateKept?: boolean;
  } | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const placeholders = useMemo(() => placeholderNames(doc, pageIndex), [doc, pageIndex]);
  const mappable = useMemo(
    () =>
      pageElementIds(doc, pageIndex)
        .map((id) => doc.elements[id]!)
        .filter((element) => isMappable(element)),
    [doc, pageIndex],
  );
  // `{{Nr}}` is the row number unless the table has such a column.
  const usage = columnUsage([...table.headers, "Nr"], placeholders, mappings);
  usage.unused = usage.unused.filter((name) => table.headers.includes(name));
  const records = useMemo(() => tableRecords(table), [table]);
  const replace = output === "replace";
  const limit = output === "files" ? MERGE_MAX_FILES : mergePageCapacity(doc, replace);
  const tooMany = records.length > limit;

  const loadTable = (next: DataTable, name: string | null) => {
    setTable(next);
    setFileName(name);
    setPreviewRow(0);
    setDone(null);
    // Image elements named like a column are mapped right away.
    const headerByKey = new Map(next.headers.map((header) => [columnKey(header), header]));
    setMappings(
      mappable.flatMap((element) => {
        const column = element.name ? headerByKey.get(columnKey(element.name)) : undefined;
        return column && (element.type === "image" || isImagePlaceholder(element))
          ? [{ elementId: element.id, column }]
          : [];
      }),
    );
    const first = next.headers.find((header) =>
      placeholders.some((name) => columnKey(name) === columnKey(header)),
    );
    setNamePattern(`${env.title} ${first ? `{{${first}}}` : "{{Nr}}"}`.trim());
  };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    try {
      const next = parseTable(await readTextFile(file));
      if (next.headers.length === 0) {
        toast.error("Die Datei enthält keine Tabelle");
        return;
      }
      loadTable(next, file.name);
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const onPasted = (text: string) => {
    setPasted(text);
    const next = parseTable(text);
    if (next.headers.length > 0) loadTable(next, null);
  };

  const emptyGrid = () => {
    const headers = placeholders.length > 0 ? placeholders : ["Name"];
    loadTable({ headers, rows: [0, 1, 2].map(() => headers.map(() => "")) }, null);
    setSource("grid");
  };

  // Image columns: look up the file names in the media library.
  const imageColumns = mappings
    .filter((mapping) => {
      const element = doc.elements[mapping.elementId];
      return element?.type === "image" || isImagePlaceholder(element);
    })
    .map((mapping) => mapping.column);
  const imageValues = [
    ...new Set(
      imageColumns.flatMap((column) =>
        records.map((record) => record[column]?.trim() ?? "").filter(Boolean),
      ),
    ),
  ];
  const imageKey = imageValues.join("\n");
  const [imagesLoading, setImagesLoading] = useState(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed by the joined values
  useEffect(() => {
    if (imageValues.length === 0) return;
    let cancelled = false;
    setImagesLoading(true);
    const timer = window.setTimeout(() => {
      resolveImageNames(env.orgId, imageValues)
        .then((found) => {
          if (!cancelled) setImages(found);
        })
        .catch((error) => toast.error(errorMessage(error)))
        .finally(() => {
          if (!cancelled) setImagesLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [imageKey, env.orgId]);
  const unresolvedImages = imageValues.filter((value) => !images.has(normalizeName(value)));

  const mergeOptions = (): MergeOptions => ({
    mappings: mappings.filter((mapping) => table.headers.includes(mapping.column)),
    resolveImage: (value) => images.get(normalizeName(value)),
    ...(output !== "files" && namePattern.trim() ? { pageName: namePattern } : {}),
  });

  const recordAt = (index: number) => ({ Nr: String(index + 1), ...records[index] });

  // One filled page for the preview (cheap: a copy of one page).
  const preview = ((): SceneDocument | null => {
    if (records.length === 0 || !doc.pages[pageIndex]) return null;
    const row = Math.min(previewRow, records.length - 1);
    const merged = mergePage(doc, pageIndex, recordAt(row), mergeOptions());
    // Back to the template ids, so the preview updates instead of remounting.
    const original = new Map([...merged.idMap].map(([oldId, newId]) => [newId, oldId]));
    const back = (id: string) => original.get(id) ?? id;
    const elements = Object.fromEntries(
      Object.values(merged.elements).map((element) => {
        const copy = { ...element, id: back(element.id) };
        if (copy.type === "group") copy.children = copy.children.map(back);
        return [copy.id, copy];
      }),
    );
    const { timeline: _timeline, ...rest } = doc;
    return {
      ...rest,
      pages: [{ ...merged.page, id: "preview", children: merged.page.children.map(back) }],
      elements,
    };
  })();

  const create = async () => {
    if (records.length === 0 || tooMany) return;
    const current = store.getState();
    const template = current.doc;
    const index = current.pageIndex;
    const options = mergeOptions();
    let filesCreated = 0;
    try {
      setBusy("Texte werden eingepasst …");
      if (output === "files") {
        const built = records.map((_, row) =>
          buildMergedDocument(template, index, recordAt(row), options),
        );
        await fitMergedTexts(
          template,
          built.map((item) => item.merged),
          { shrink },
        );
        for (const [row, item] of built.entries()) {
          normalizeMergedGroups(item.doc, [item.merged]);
          setBusy(`Datei ${row + 1} von ${built.length} wird angelegt …`);
          await client.documents.create({
            orgId: env.orgId,
            projectId: env.projectId,
            title: mergeTitle(namePattern, recordAt(row), row, env.title || "Serie"),
            content: item.doc,
          });
          filesCreated++;
        }
        await queryClient.invalidateQueries({ queryKey: orpc.documents.key() });
        setDone({ kind: "files", count: built.length });
        toast.success(`${built.length} Dateien angelegt`);
      } else {
        const pages = records.map((_, row) => mergePage(template, index, recordAt(row), options));
        const { shrunk } = await fitMergedTexts(template, pages, { shrink });
        let first = -1;
        store.getState().update((draft) => {
          first = applyMergedPages(draft, index, pages, replace);
        });
        if (first >= 0) store.getState().setPage(first);
        setDone({ kind: "pages", count: pages.length, templateKept: !replace });
        toast.success(
          `${pages.length} Seiten angelegt${shrunk > 0 ? ` – ${shrunk}× Schrift verkleinert` : ""}`,
        );
      }
    } catch (error) {
      // E.g. the storage quota of the plan: say how far it got.
      toast.error(
        filesCreated > 0
          ? `${filesCreated} von ${records.length} Dateien angelegt – ${errorMessage(error)}`
          : errorMessage(error),
      );
      if (filesCreated > 0) {
        void queryClient.invalidateQueries({ queryKey: orpc.documents.key() });
      }
    } finally {
      setBusy(null);
    }
  };

  if (done) {
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-3 rounded-lg bg-emerald-50 p-4 text-sm text-emerald-900">
          <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-emerald-600" />
          <div>
            <p className="font-semibold">
              {done.kind === "pages"
                ? `${done.count} Seiten wurden angelegt.`
                : `${done.count} Dateien wurden im Projekt angelegt.`}
            </p>
            <p className="mt-1 text-emerald-800">
              {done.kind === "pages"
                ? "Mit „Rückgängig“ (⌘Z) entfernst du die ganze Serie in einem Schritt."
                : "Du findest sie im Projekt neben dieser Datei."}
            </p>
            {done.templateKept ? (
              <p className="mt-1 text-emerald-800">
                Tipp: Die Vorlagenseite mit den Platzhaltern ist noch da und würde mit exportiert –
                lösche sie vorher oder wähle „Aktuelle Seite ersetzen“.
              </p>
            ) : null}
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Schließen
          </Button>
          {done.kind === "pages" ? (
            <Button
              onClick={() => {
                onClose();
                onExport();
              }}
            >
              <Download /> Jetzt exportieren
            </Button>
          ) : (
            <Button
              onClick={() => {
                onClose();
                void navigate({
                  to: "/o/$orgId/projects/$projectId",
                  params: { orgId: env.orgId, projectId: env.projectId },
                });
              }}
            >
              Zum Projekt
            </Button>
          )}
        </DialogFooter>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 1. Data */}
      <section className="space-y-3">
        <StepTitle number={1} title="Daten" />
        <div className="flex flex-wrap gap-1 rounded-lg bg-zinc-100 p-1 text-sm">
          {(
            [
              ["file", "CSV-Datei"],
              ["paste", "Aus Excel einfügen"],
              ["grid", "Tabelle eingeben"],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() =>
                id === "grid" && table.headers.length === 0 ? emptyGrid() : setSource(id)
              }
              className={cn(
                "rounded-md px-3 py-1.5 font-medium transition",
                source === id
                  ? "bg-white text-zinc-900 shadow-sm"
                  : "text-zinc-600 hover:text-zinc-900",
              )}
            >
              {label}
            </button>
          ))}
        </div>
        {source === "file" ? (
          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={fileInput}
              type="file"
              accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values,text/plain"
              className="hidden"
              onChange={(event) => {
                void onFile(event.target.files?.[0]);
                event.target.value = "";
              }}
            />
            <Button variant="secondary" onClick={() => fileInput.current?.click()}>
              <FileUp /> CSV-Datei wählen …
            </Button>
            <span className="text-xs text-zinc-500">
              {fileName
                ? `„${fileName}“ – ${table.rows.length} Zeilen, ${table.headers.length} Spalten`
                : "Trennzeichen (Semikolon, Komma, Tab) und Umlaute werden automatisch erkannt. Die erste Zeile enthält die Spaltennamen."}
            </span>
          </div>
        ) : null}
        {source === "paste" ? (
          <Textarea
            value={pasted}
            onChange={(event) => onPasted(event.target.value)}
            placeholder={
              "Zellen in Excel, Numbers oder Google Tabellen markieren, kopieren und hier einfügen.\nDie erste Zeile enthält die Spaltennamen, z. B.:\nName\tCode"
            }
            className="min-h-28 font-mono text-xs"
          />
        ) : null}
        {table.headers.length > 0 ? (
          <DataGrid table={table} onChange={(next) => setTable(next)} />
        ) : source === "grid" ? null : (
          <p className="text-xs text-zinc-500">
            Noch keine Daten.{" "}
            <button type="button" className="text-brand-600 hover:underline" onClick={emptyGrid}>
              Leere Tabelle mit den Platzhaltern der Seite anlegen
            </button>
          </p>
        )}
      </section>

      {/* 2. Mapping */}
      <section className="space-y-3">
        <StepTitle number={2} title="Zuordnung" />
        {placeholders.length === 0 ? (
          <p className="text-sm text-zinc-500">
            Auf dieser Seite gibt es noch keine Platzhalter. Schreibe z. B.{" "}
            <code className="rounded bg-zinc-100 px-1">{"{{Name}}"}</code> in einen Text oder{" "}
            <code className="rounded bg-zinc-100 px-1">{"https://beispiel.de/?id={{ID}}"}</code> in
            einen QR-Code – oder ordne unten Elemente direkt einer Spalte zu.
          </p>
        ) : (
          <div className="flex flex-wrap items-center gap-1.5 text-xs">
            <span className="text-zinc-500">Platzhalter:</span>
            {placeholders.map((name) => {
              const missing = table.headers.length > 0 && usage.missing.includes(name);
              return (
                <span
                  key={name}
                  title={
                    missing ? "Keine passende Spalte – der Platzhalter bleibt stehen" : undefined
                  }
                  className={cn(
                    "rounded-full px-2 py-0.5 font-medium",
                    missing ? "bg-amber-100 text-amber-800" : "bg-zinc-100 text-zinc-700",
                    table.headers.length > 0 && !missing && "bg-emerald-100 text-emerald-800",
                  )}
                >
                  {`{{${name}}}`}
                </span>
              );
            })}
          </div>
        )}
        {table.headers.length > 0 && usage.missing.length > 0 ? (
          <Notice>
            Keine Spalte für {usage.missing.map((name) => `„${name}“`).join(", ")} – diese
            Platzhalter bleiben unverändert stehen.
          </Notice>
        ) : null}
        {table.headers.length > 0 && usage.unused.length > 0 ? (
          <p className="text-xs text-zinc-500">
            Nicht verwendete Spalten: {usage.unused.map((name) => `„${name}“`).join(", ")}
          </p>
        ) : null}
        <div className="space-y-2">
          {mappings.map((mapping, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: mappings are edited in place
            <div key={index} className="flex items-center gap-2">
              <Select
                aria-label="Element"
                value={mapping.elementId}
                onChange={(event) =>
                  setMappings(
                    mappings.map((item, at) =>
                      at === index ? { ...item, elementId: event.target.value } : item,
                    ),
                  )
                }
              >
                {mappable.map((element) => (
                  <option key={element.id} value={element.id}>
                    {elementLabel(element)}
                  </option>
                ))}
              </Select>
              <span className="shrink-0 text-xs text-zinc-500">bekommt Spalte</span>
              <Select
                aria-label="Spalte"
                value={mapping.column}
                onChange={(event) =>
                  setMappings(
                    mappings.map((item, at) =>
                      at === index ? { ...item, column: event.target.value } : item,
                    ),
                  )
                }
              >
                {table.headers.map((header) => (
                  <option key={header} value={header}>
                    {header}
                  </option>
                ))}
              </Select>
              <button
                type="button"
                aria-label="Zuordnung entfernen"
                className="rounded-md p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
                onClick={() => setMappings(mappings.filter((_, at) => at !== index))}
              >
                <X className="size-4" />
              </button>
            </div>
          ))}
          <Button
            variant="ghost"
            size="sm"
            disabled={mappable.length === 0 || table.headers.length === 0}
            onClick={() =>
              setMappings([
                ...mappings,
                { elementId: mappable[0]!.id, column: usage.unused[0] ?? table.headers[0]! },
              ])
            }
          >
            <Plus /> Element einer Spalte zuordnen
          </Button>
          <p className="text-xs text-zinc-500">
            Texte bekommen den Zellinhalt als ganzen Text, QR-Codes als Inhalt. Für Bilder und
            Bildplatzhalter enthält die Spalte den Dateinamen eines Bildes aus deinen Medien (z. B.
            „anna.jpg“).
          </p>
          {imageValues.length > 0 && !imagesLoading && unresolvedImages.length > 0 ? (
            <Notice>
              Nicht in den Medien gefunden:{" "}
              {unresolvedImages
                .slice(0, 6)
                .map((value) => `„${value}“`)
                .join(", ")}
              {unresolvedImages.length > 6 ? ` und ${unresolvedImages.length - 6} weitere` : ""} –
              dort bleibt das Bild der Vorlage.
            </Notice>
          ) : null}
        </div>
      </section>

      {/* 3. Output */}
      <section className="space-y-3">
        <StepTitle number={3} title="Ergebnis" />
        <div className="grid gap-4 md:grid-cols-[1fr_240px]">
          <div className="space-y-3">
            <div className="grid gap-2 sm:grid-cols-3">
              {(
                [
                  ["after", "Als Seiten in diesem Dokument", "Nach der aktuellen Seite"],
                  ["replace", "Aktuelle Seite ersetzen", "Die Vorlagenseite wird ersetzt"],
                  ["files", "Als einzelne Dateien", "Eine Datei je Zeile im Projekt"],
                ] as const
              ).map(([id, label, hint]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setOutput(id)}
                  className={cn(
                    "rounded-lg border px-3 py-2 text-left transition",
                    output === id
                      ? "border-brand-500 bg-brand-50 ring-2 ring-brand-500/20"
                      : "border-zinc-200 hover:border-zinc-300",
                  )}
                >
                  <span className="block text-sm font-medium">{label}</span>
                  <span className="block text-xs text-zinc-500">{hint}</span>
                </button>
              ))}
            </div>
            <label className="block space-y-1.5">
              <span className="text-sm font-semibold text-zinc-700">
                {output === "files" ? "Dateiname" : "Seitenname"}
              </span>
              <Input
                value={namePattern}
                onChange={(event) => setNamePattern(event.target.value)}
                placeholder="z. B. Gutschein {{Name}}"
              />
              <span className="block text-xs text-zinc-500">
                Platzhalter werden je Zeile ersetzt; <code>{"{{Nr}}"}</code> ist die Zeilennummer.
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm text-zinc-700">
              <input
                type="checkbox"
                className="mt-0.5 size-4 accent-brand-600"
                checked={shrink}
                onChange={(event) => setShrink(event.target.checked)}
              />
              <span>
                Schrift verkleinern, wenn der Text nicht passt
                <span className="block text-xs text-zinc-500">
                  Lange Werte werden bis auf die halbe Schriftgröße verkleinert, damit sie in die
                  Höhe des Textfelds der Vorlage passen. Was dann noch zu lang ist, lässt das
                  Textfeld nach unten wachsen.
                </span>
              </span>
            </label>
            {tooMany ? (
              <Notice>
                {output === "files"
                  ? `Es können höchstens ${MERGE_MAX_FILES} Dateien auf einmal angelegt werden – die Tabelle hat ${records.length} Zeilen.`
                  : `Ein Dokument hat höchstens ${MERGE_MAX_PAGES} Seiten – hier ist noch Platz für ${limit}, die Tabelle hat ${records.length} Zeilen. Wähle „Als einzelne Dateien“ oder teile die Tabelle auf.`}
              </Notice>
            ) : null}
          </div>
          <div className="space-y-2">
            {preview ? (
              <>
                <div className="overflow-hidden rounded-lg border border-zinc-200 bg-zinc-50">
                  <ScenePreview
                    document={preview}
                    width={238}
                    resolveAsset={env.resolveAsset}
                    animate={false}
                  />
                </div>
                <div className="flex items-center justify-between text-xs text-zinc-500">
                  <button
                    type="button"
                    aria-label="Vorherige Zeile"
                    className="rounded p-1 hover:bg-zinc-100 disabled:opacity-40"
                    disabled={previewRow <= 0}
                    onClick={() => setPreviewRow(previewRow - 1)}
                  >
                    <ChevronLeft className="size-4" />
                  </button>
                  <span className="tabular-nums">
                    Vorschau Zeile {Math.min(previewRow, records.length - 1) + 1} von{" "}
                    {records.length}
                  </span>
                  <button
                    type="button"
                    aria-label="Nächste Zeile"
                    className="rounded p-1 hover:bg-zinc-100 disabled:opacity-40"
                    disabled={previewRow >= records.length - 1}
                    onClick={() => setPreviewRow(previewRow + 1)}
                  >
                    <ChevronRight className="size-4" />
                  </button>
                </div>
              </>
            ) : (
              <div className="flex aspect-square items-center justify-center rounded-lg border border-dashed border-zinc-300 p-4 text-center text-xs text-zinc-400">
                Vorschau erscheint, sobald Daten da sind
              </div>
            )}
          </div>
        </div>
      </section>

      <DialogFooter>
        {busy ? <span className="mr-auto self-center text-sm text-zinc-500">{busy}</span> : null}
        <Button variant="secondary" onClick={onClose} disabled={Boolean(busy)}>
          Abbrechen
        </Button>
        <Button
          loading={Boolean(busy)}
          disabled={records.length === 0 || tooMany || Boolean(busy)}
          onClick={() => void create()}
        >
          {records.length === 0
            ? "Serie erstellen"
            : output === "files"
              ? `${records.length} Dateien erstellen`
              : `${records.length} Seiten erstellen`}
        </Button>
      </DialogFooter>
    </div>
  );
}

function StepTitle({ number, title }: { number: number; title: string }) {
  return (
    <h3 className="flex items-center gap-2 text-sm font-semibold text-zinc-900">
      <span className="flex size-5 items-center justify-center rounded-full bg-brand-600 text-[11px] text-white">
        {number}
      </span>
      {title}
    </h3>
  );
}

function Notice({ children }: { children: React.ReactNode }) {
  return (
    <p className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
      <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-amber-600" />
      <span>{children}</span>
    </p>
  );
}

/** Small editable grid: headers and cells, add/remove rows and columns. */
function DataGrid({ table, onChange }: { table: DataTable; onChange: (table: DataTable) => void }) {
  const shown = table.rows.slice(0, GRID_ROWS_SHOWN);
  const setCell = (row: number, column: number, value: string) =>
    onChange({
      headers: table.headers,
      rows: table.rows.map((cells, at) =>
        at === row ? cells.map((cell, index) => (index === column ? value : cell)) : cells,
      ),
    });
  const setHeader = (column: number, value: string) =>
    onChange({
      headers: table.headers.map((header, index) => (index === column ? value : header)),
      rows: table.rows,
    });
  const cell =
    "w-full min-w-24 bg-transparent px-2 py-1 text-xs outline-none focus:bg-brand-50 focus:ring-1 focus:ring-brand-500";
  return (
    <div className="space-y-2">
      <div className="max-h-64 overflow-auto rounded-lg border border-zinc-200">
        <table className="w-full border-collapse text-left">
          <thead className="sticky top-0 bg-zinc-50">
            <tr>
              <th className="w-8 border-b border-zinc-200 px-2 text-[11px] font-normal text-zinc-400">
                #
              </th>
              {table.headers.map((header, column) => (
                <th
                  // biome-ignore lint/suspicious/noArrayIndexKey: columns have no ids
                  key={column}
                  className="border-b border-l border-zinc-200 font-semibold"
                >
                  <input
                    aria-label={`Spaltenname ${column + 1}`}
                    className={cn(cell, "font-semibold")}
                    value={header}
                    onChange={(event) => setHeader(column, event.target.value)}
                  />
                </th>
              ))}
              <th className="w-8 border-b border-zinc-200" />
            </tr>
          </thead>
          <tbody>
            {shown.map((cells, row) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: rows have no ids
              <tr key={row} className="odd:bg-white even:bg-zinc-50/50">
                <td className="px-2 text-[11px] text-zinc-400 tabular-nums">{row + 1}</td>
                {cells.map((value, column) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: columns have no ids
                  <td key={column} className="border-l border-zinc-100">
                    <input
                      aria-label={`${table.headers[column]} in Zeile ${row + 1}`}
                      className={cell}
                      value={value}
                      onChange={(event) => setCell(row, column, event.target.value)}
                    />
                  </td>
                ))}
                <td>
                  <button
                    type="button"
                    aria-label={`Zeile ${row + 1} entfernen`}
                    className="rounded p-1 text-zinc-300 hover:bg-zinc-100 hover:text-zinc-600"
                    onClick={() =>
                      onChange({
                        headers: table.headers,
                        rows: table.rows.filter((_, at) => at !== row),
                      })
                    }
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-500">
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            onChange({ headers: table.headers, rows: [...table.rows, table.headers.map(() => "")] })
          }
        >
          <Plus /> Zeile
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            onChange({
              headers: [...table.headers, `Spalte ${table.headers.length + 1}`],
              rows: table.rows.map((cells) => [...cells, ""]),
            })
          }
        >
          <Plus /> Spalte
        </Button>
        <span>
          {table.rows.length} Zeilen
          {table.rows.length > GRID_ROWS_SHOWN
            ? ` (die ersten ${GRID_ROWS_SHOWN} werden angezeigt)`
            : ""}
        </span>
      </div>
    </div>
  );
}
